/**
 * 📡 Connection Monitor — WebSocket Sağlık Kontrolü ve Kademeli Reconnect
 *
 * Reconnect Stratejisi:
 * Deneme #1: 2sn → #2: 4sn → #3: 8sn → #4: 16sn → #5: 30sn → DEGRADED
 * DEGRADED modda 60sn'de bir arka plan reconnect denemesi
 */

import { RealtimeChannel } from '@supabase/supabase-js'
import {
  ConnectionState,
  RECONNECT_DELAYS,
  MAX_RECONNECT_ATTEMPTS,
  HEARTBEAT_INTERVAL,
  BACKGROUND_RECONNECT_INTERVAL,
} from './types'
import { realtimeLogger } from './logger'

type OnStateChange = (state: ConnectionState) => void
type OnReconnectNeeded = () => Promise<boolean>

export class ConnectionMonitor {
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private backgroundTimer: ReturnType<typeof setInterval> | null = null
  private reconnectAttempt = 0
  private currentState: ConnectionState = 'idle'
  private onStateChange: OnStateChange
  private onReconnectNeeded: OnReconnectNeeded
  private channels: Map<string, RealtimeChannel> = new Map()

  constructor(onStateChange: OnStateChange, onReconnectNeeded: OnReconnectNeeded) {
    this.onStateChange = onStateChange
    this.onReconnectNeeded = onReconnectNeeded
  }

  /** Kanal kaydı (heartbeat izleme için) */
  registerChannel(name: string, channel: RealtimeChannel) {
    this.channels.set(name, channel)
  }

  /** Tüm kanal kayıtlarını temizle */
  clearChannels() {
    this.channels.clear()
  }

  /** Heartbeat başlat — 30sn'de bir kanal durumlarını kontrol et */
  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.checkChannelHealth()
    }, HEARTBEAT_INTERVAL)
  }

  /** Heartbeat durdur */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /** Kanal sağlık kontrolü */
  private checkChannelHealth() {
    const states: Record<string, string> = {}
    let hasError = false

    for (const [name, channel] of this.channels) {
      const state = (channel as any)?.state ?? 'unknown'
      states[name] = state
      if (state === 'closed' || state === 'errored') {
        hasError = true
      }
    }

    realtimeLogger.heartbeat(states)

    if (hasError && this.currentState === 'connected') {
      this.initiateReconnect()
    }
  }

  /** Subscription status callback'i */
  handleSubscriptionStatus(channelName: string, status: string) {
    switch (status) {
      case 'SUBSCRIBED':
        realtimeLogger.channelSubscribed(channelName)
        this.onChannelReady()
        break
      case 'CHANNEL_ERROR':
        realtimeLogger.channelError(channelName)
        this.initiateReconnect()
        break
      case 'TIMED_OUT':
        realtimeLogger.channelTimeout(channelName)
        this.initiateReconnect()
        break
      case 'CLOSED':
        if (this.currentState !== 'idle') {
          this.initiateReconnect()
        }
        break
    }
  }

  /** Tüm kanallar hazır mı kontrol et */
  private onChannelReady() {
    let allReady = true
    for (const [, channel] of this.channels) {
      const state = (channel as any)?.state
      if (state !== 'joined') {
        allReady = false
        break
      }
    }

    if (allReady && this.channels.size > 0) {
      this.reconnectAttempt = 0
      this.stopBackgroundReconnect()
      this.setState('connected')
    }
  }

  /** Kademeli reconnect başlat */
  initiateReconnect() {
    if (this.currentState === 'reconnecting' || this.currentState === 'idle') return

    this.setState('reconnecting')
    this.attemptReconnect()
  }

  /** Reconnect denemesi — exponential backoff */
  private async attemptReconnect() {
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      this.setState('degraded')
      this.startBackgroundReconnect()
      return
    }

    const delay = RECONNECT_DELAYS[
      Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)
    ]
    realtimeLogger.reconnecting(this.reconnectAttempt + 1, delay)

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempt++
      const success = await this.onReconnectNeeded()
      if (!success) {
        this.attemptReconnect()
      }
      // success ise onChannelReady() → connected olacak
    }, delay)
  }

  /** DEGRADED modda 60sn'de bir arka plan reconnect */
  private startBackgroundReconnect() {
    this.stopBackgroundReconnect()
    this.backgroundTimer = setInterval(async () => {
      realtimeLogger.reconnecting(this.reconnectAttempt + 1, BACKGROUND_RECONNECT_INTERVAL)
      this.reconnectAttempt = 0 // sıfırla, 5 deneme daha yap
      const success = await this.onReconnectNeeded()
      if (success) {
        this.stopBackgroundReconnect()
      }
    }, BACKGROUND_RECONNECT_INTERVAL)
  }

  /** Arka plan reconnect durdur */
  private stopBackgroundReconnect() {
    if (this.backgroundTimer) {
      clearInterval(this.backgroundTimer)
      this.backgroundTimer = null
    }
  }

  /** State değiştir */
  private setState(state: ConnectionState) {
    if (this.currentState === state) return
    this.currentState = state
    this.onStateChange(state)
  }

  /** Mevcut state */
  getState(): ConnectionState {
    return this.currentState
  }

  /** Reset — bağlantı kapandığında */
  reset() {
    this.stopHeartbeat()
    this.stopBackgroundReconnect()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempt = 0
    this.channels.clear()
    this.setState('idle')
  }
}
